import { createContext, useContext, type ImgHTMLAttributes, type ReactNode } from "react";
import { brand, type BrandSurface, type ResponsiveRasterAsset } from "../config/brand";

const BrandSurfaceContext = createContext<BrandSurface>("light");

export function BrandSurfaceProvider({
  surface,
  children,
}: {
  surface: BrandSurface;
  children: ReactNode;
}) {
  return (
    <BrandSurfaceContext.Provider value={surface}>
      {children}
    </BrandSurfaceContext.Provider>
  );
}

function useBrandSurface(surface?: BrandSurface) {
  const contextSurface = useContext(BrandSurfaceContext);
  return surface ?? contextSurface;
}

interface BrandImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> {
  asset: ResponsiveRasterAsset;
}

function BrandImage({ asset, ...props }: BrandImageProps) {
  return (
    <img
      src={asset.oneX}
      srcSet={`${asset.oneX} 1x, ${asset.twoX} 2x, ${asset.fourX} 4x`}
      {...props}
    />
  );
}

export function BrandLockup({
  surface,
  compact = false,
  withoutTagline = false,
  className = "",
}: {
  surface?: BrandSurface;
  compact?: boolean;
  withoutTagline?: boolean;
  className?: string;
}) {
  const activeSurface = useBrandSurface(surface);
  const asset = compact
    ? brand.assets.product.compact[activeSurface]
    : withoutTagline
      ? brand.assets.product.horizontalWithoutTagline[activeSurface]
      : brand.assets.product.horizontal[activeSurface];
  return (
    <BrandImage
      asset={asset}
      className={`brand-lockup ${className}`.trim()}
      alt={brand.officialProductName}
      draggable={false}
    />
  );
}

export function BibleAloneSymbol({
  surface,
  className = "",
  decorative = false,
}: {
  surface?: BrandSurface;
  className?: string;
  decorative?: boolean;
}) {
  const activeSurface = useBrandSurface(surface);
  return (
    <img
      className={`bible-alone-symbol ${className}`.trim()}
      src={brand.assets.product.symbol[activeSurface]}
      alt={decorative ? "" : brand.officialProductName}
      aria-hidden={decorative || undefined}
      draggable={false}
    />
  );
}

export function SolaMark({
  surface,
  className = "",
  decorative = false,
}: {
  surface?: BrandSurface;
  className?: string;
  decorative?: boolean;
}) {
  const activeSurface = useBrandSurface(surface);
  return (
    <img
      className={`sola-mark ${className}`.trim()}
      src={brand.assets.assistant.symbol[activeSurface]}
      alt={decorative ? "" : brand.assistantName}
      aria-hidden={decorative || undefined}
      draggable={false}
    />
  );
}

export function SolaLabel({
  surface,
  children,
  className = "",
}: {
  surface?: BrandSurface;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={`sola-label ${className}`.trim()}>
      <SolaMark {...(surface ? { surface } : {})} decorative />
      <span>{children}</span>
    </span>
  );
}
